'use client';

import type { CategoryKey, SituationKey, TypeKey } from '@caverne/shared';

/**
 * Pictogrammes du matériel, repris tels quels de `design_handoff/Inventaire matériel.dc.html`
 * (constantes `TI` et `CI`).
 *
 * Untitled UI n'a d'équivalent que pour une poignée d'entre eux (ordinateur, écran, imprimante) :
 * il n'existe ni fauteuil, ni frigo, ni cave à vin, ni panière dans sa bibliothèque. Plutôt que
 * de panacher deux styles de trait, on garde la série complète de la maquette — même grille 24,
 * même épaisseur 1,7 px, même `currentColor` — pour que l'ensemble reste cohérent.
 */

const STROKE_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const TYPE_PATHS: Record<TypeKey, string> = {
  ORDI: '<rect x="3" y="4" width="18" height="12" rx="1.5"/><path d="M2 20h20M2 20l1.5-2M22 20l-1.5-2"/>',
  TABLETTE: '<rect x="4" y="2" width="16" height="20" rx="2.5"/><path d="M11 18h2"/>',
  TEL: '<rect x="7" y="2" width="10" height="20" rx="2.5"/><path d="M11 18h2"/>',
  ECRAN: '<rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>',
  CLE4G:
    '<path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><path d="M12 20h.01"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/>',
  ROUTEUR:
    '<rect x="3" y="13" width="18" height="7" rx="1.5"/><path d="M7 17h.01M11 17h6"/><path d="M8 9.5a5 5 0 0 1 8 0M5.5 6.8a9 9 0 0 1 13 0"/>',
  TPE: '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 7h8M8 11h8M9 16h6"/>',
  IMPRIMANTE:
    '<path d="M6 9V2h12v7"/><rect x="6" y="14" width="12" height="8" rx="1"/><path d="M6 18H4a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-2"/>',
  FAUTEUIL:
    '<path d="M7 18v2M17 18v2"/><path d="M5 11a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z"/><path d="M8 9V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3"/>',
  BADGE_BAS:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18"/><circle cx="8" cy="14" r="1.5"/><path d="M13 13h5M13 16h3"/>',
  BADGE_BUR:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18"/><circle cx="8" cy="14" r="1.5"/><path d="M13 13h5M13 16h3"/>',
  FAUTEUIL_ERGO:
    '<path d="M7 18v2M17 18v2"/><path d="M5 11a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z"/><path d="M8 9V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3"/>',
  SOURIS_ERGO: '<rect x="7" y="3" width="10" height="18" rx="5"/><path d="M12 7v4"/>',
  ADAPTE_AUTRE:
    '<circle cx="12" cy="4" r="1.6"/><path d="M5 8.5l7 1 7-1"/><path d="M12 9.5V15l-3 5M12 15l3 5"/>',
  VITRINE: '<rect x="6" y="2" width="12" height="20" rx="2"/><path d="M6 10h12M10 5v2M10 13v3"/>',
  FRIGO: '<rect x="6" y="2" width="12" height="20" rx="2"/><path d="M6 10h12M10 5v2M10 13v3"/>',
  CAVE_VIN: '<path d="M8 22h8M12 15v7"/><path d="M7 3h10l-1 6a4 4 0 0 1-8 0z"/>',
  CAVE_CHOCO:
    '<rect x="3" y="8" width="18" height="13" rx="1.5"/><path d="M3 8l3-5h12l3 5M12 3v5M3 13h18"/>',
  CIREUSE: '<circle cx="12" cy="13" r="6"/><path d="M12 7V4M9 4h6"/>',
  PLASTIF: '<rect x="3" y="7" width="18" height="6" rx="1.5"/><path d="M7 13v4M17 13v4M12 13v6"/>',
  TOTEM: '<rect x="8" y="2" width="8" height="20" rx="1.5"/><path d="M11 19h2"/>',
  TV: '<rect x="2" y="4" width="20" height="13" rx="2"/><path d="M8 21h8M12 17v4"/>',
  SITE_AUTRE: '<rect x="3" y="7" width="18" height="13" rx="1.5"/><path d="M3 7l9-4 9 4M12 3v17"/>',
  CHARRIOT:
    '<circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2 3h3l2.4 12.3a1.5 1.5 0 0 0 1.5 1.2h8a1.5 1.5 0 0 0 1.5-1.2L21 7H6"/>',
  GLACIERE: '<rect x="3" y="8" width="18" height="11" rx="1.5"/><path d="M3 12h18M9 8V6h6v2"/>',
  PANIERE:
    '<path d="M4 9h16l-1.5 10.5a2 2 0 0 1-2 1.5H7.5a2 2 0 0 1-2-1.5z"/><path d="M9 9 7 4M15 9l2-5M3 9h18"/>',
};

const CATEGORY_PATHS: Record<CategoryKey, string> = {
  INFO: '<rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>',
  NET: '<path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><path d="M12 20h.01"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/>',
  BUREAU: '<rect x="3" y="4" width="18" height="4" rx="1"/><path d="M5 8v12M19 8v12M5 14h14"/>',
  ACCES: '<circle cx="8" cy="8" r="5"/><path d="M11.5 11.5 21 21M17 17l2-2"/>',
  ADAPTE: '<path d="M19 14c1.5-1.5 3-3.3 3-5.5A4.5 4.5 0 0 0 12 5 4.5 4.5 0 0 0 2 8.5c0 2.2 1.5 4 3 5.5l7 7z"/>',
  SITE: '<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01"/>',
  ITIN: '<circle cx="6" cy="19" r="3"/><circle cx="18" cy="5" r="3"/><path d="M9 19h6a4 4 0 0 0 0-8H9a4 4 0 0 1 0-8"/>',
};

/** Situations de l'étape 1 de l'assistant (`sitIcons` du prototype). */
const SITUATION_PATHS: Record<SituationKey, string> = {
  FIXE: CATEGORY_PATHS.SITE,
  // La maquette réutilise ici le pictogramme de l'équipement itinérant : même idée de déplacement.
  MULTI: CATEGORY_PATHS.ITIN,
  HOME: '<path d="M3 9.5 12 3l9 6.5"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/>',
  SIEGE:
    '<line x1="3" y1="22" x2="21" y2="22"/><path d="M4 22V10l8-5 8 5v12"/><line x1="9" y1="22" x2="9" y2="14"/><line x1="15" y1="22" x2="15" y2="14"/>',
};

interface IconProps {
  className?: string;
}

/** Pictogramme d'un type de matériel (ordinateur, cave à vin, panière…). */
export function EquipmentIcon({ typeKey, className }: IconProps & { typeKey: TypeKey }) {
  return (
    <svg
      {...STROKE_PROPS}
      className={className}
      aria-hidden="true"
      // Tracés statiques du design system, jamais dérivés d'une saisie utilisateur.
      dangerouslySetInnerHTML={{ __html: TYPE_PATHS[typeKey] }}
    />
  );
}

/** Pictogramme d'une catégorie (Informatique, Équipement de site…). */
export function CategoryIcon({ categoryKey, className }: IconProps & { categoryKey: CategoryKey }) {
  return (
    <svg
      {...STROKE_PROPS}
      className={className}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: CATEGORY_PATHS[categoryKey] }}
    />
  );
}

/** Pictogramme d'une situation (site fixe, multi-site, home office, siège). */
export function SituationIcon({ situation, className }: IconProps & { situation: SituationKey }) {
  return (
    <svg
      {...STROKE_PROPS}
      className={className}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: SITUATION_PATHS[situation] }}
    />
  );
}
